import React, { useState, useRef } from "react";
import api from "@/lib/api";

// Pagina per confrontare un export Segremat (.xls/.xlsx AG1E2) con l'anagrafica
// alunni del DB e scaricare un report Excel multi-foglio. READ-ONLY.
export default function VerificaAnagraficaPage() {
  const annoCorrente = new Date().getMonth() >= 8
    ? new Date().getFullYear() + 1
    : new Date().getFullYear();
  const [anno, setAnno] = useState(annoCorrente);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  const annoLabel = (y) => `${y - 1}/${String(y).slice(-2)}`;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setErr("Seleziona un file Excel"); return; }
    setBusy(true); setErr(""); setStats(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("anno", String(anno));
      const r = await api.post("/alunni/verifica-anagrafica-xls", fd, {
        responseType: "blob",
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Estrai header diagnostici per mostrare il riepilogo a video
      const h = r.headers;
      setStats({
        excelRows: Number(h["x-excel-rows"] || 0),
        dbRows: Number(h["x-db-rows"] || 0),
        match: Number(h["x-match"] || 0),
        onlyExcel: Number(h["x-only-excel"] || 0),
        onlyDb: Number(h["x-only-db"] || 0),
        differences: Number(h["x-differences"] || 0),
      });
      // Scarica il file
      const blob = new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
      a.download = `verifica_anagrafica_${anno}_${ts}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (ex) {
      // se la risposta è blob ma errore, prova a leggerla come JSON
      let msg = ex.response?.data?.detail || ex.message || "Errore";
      if (ex.response?.data instanceof Blob) {
        try {
          const t = await ex.response.data.text();
          const j = JSON.parse(t);
          msg = j.detail || msg;
        } catch { /* ignore parse */ }
      }
      setErr(String(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1100 }} data-testid="verifica-anagrafica-page">
      <h1 style={{ fontSize: 24, fontWeight: 800, color: "#0d47a1", marginBottom: 6 }}>
        🔍 Verifica Anagrafica vs Excel
      </h1>
      <p style={{ color: "#546e7a", fontSize: 13, marginTop: 0 }}>
        Carica un export Segremat (file <b>BSSP*_AG1E2_*.xls</b>) e confrontalo con
        l'anagrafica alunni del database. Operazione <b>READ-ONLY</b>: nessuna
        modifica viene applicata. Il report scaricato contiene 4 fogli con
        l'elenco completo di alunni da aggiungere, alunni assenti dal file e
        differenze campo per campo.
      </p>

      <form onSubmit={onSubmit} style={{
        marginTop: 20, padding: 20, background: "#f5f7fa",
        border: "1px solid #cfd8dc", borderRadius: 6, display: "grid",
        gridTemplateColumns: "auto 1fr auto", gap: 14, alignItems: "center",
      }}>
        <label style={{ fontWeight: 700, color: "#37474f", fontSize: 12 }}>
          ANNO SCOLASTICO (DB)
        </label>
        <select value={anno} onChange={(e) => setAnno(Number(e.target.value))}
          data-testid="verifica-anno"
          style={{ padding: "8px 12px", fontSize: 14, fontWeight: 700,
                   border: "2px solid #0d47a1", borderRadius: 3, width: 200 }}>
          {[-1, 0, 1].map((off) => {
            const y = annoCorrente + off;
            return <option key={y} value={y}>{annoLabel(y)} (anno={y})</option>;
          })}
        </select>
        <span style={{ fontSize: 11, color: "#888", fontStyle: "italic" }}>
          il file dovrebbe coprire questo anno
        </span>

        <label style={{ fontWeight: 700, color: "#37474f", fontSize: 12 }}>
          FILE EXCEL (.xls/.xlsx)
        </label>
        <input ref={inputRef} type="file" accept=".xls,.xlsx"
          data-testid="verifica-file"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          style={{ padding: "8px", fontSize: 13, background: "white",
                   border: "1px solid #b0bec5", borderRadius: 3 }} />
        <span style={{ fontSize: 12, color: file ? "#1b5e20" : "#888" }}>
          {file ? `✓ ${file.name} (${Math.round(file.size / 1024)} KB)` : "nessun file selezionato"}
        </span>

        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10, marginTop: 6 }}>
          <button type="submit" disabled={busy || !file}
            data-testid="verifica-submit"
            style={{
              padding: "10px 22px", background: "#0d47a1", color: "white",
              border: "none", fontSize: 13, fontWeight: 800, cursor: busy ? "wait" : "pointer",
              borderRadius: 3, letterSpacing: 0.3, opacity: (!file || busy) ? 0.6 : 1,
            }}>
            {busy ? "⏳ Elaborazione…" : "📊 ESEGUI CONFRONTO E SCARICA REPORT"}
          </button>
          {file && (
            <button type="button" onClick={() => { setFile(null); setStats(null); setErr(""); if (inputRef.current) inputRef.current.value = ""; }}
              style={{ padding: "10px 16px", background: "white", color: "#37474f",
                       border: "1px solid #b0bec5", fontSize: 12, cursor: "pointer", borderRadius: 3 }}>
              Azzera
            </button>
          )}
        </div>
      </form>

      {err && (
        <div data-testid="verifica-error"
          style={{ marginTop: 16, padding: 12, background: "#ffebee",
                   border: "1px solid #ef9a9a", color: "#b71c1c",
                   fontSize: 13, borderRadius: 3 }}>
          ⚠ {err}
        </div>
      )}

      {stats && (
        <div data-testid="verifica-stats"
          style={{ marginTop: 18, padding: 16, background: "#e3f2fd",
                   border: "1px solid #90caf9", borderRadius: 6 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#0d47a1", marginBottom: 10 }}>
            ✅ Report generato e scaricato
          </div>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <tbody>
              <StatRow label="📋 Alunni nel file Excel" value={stats.excelRows} />
              <StatRow label={`📋 Alunni nel DB (anno ${anno})`} value={stats.dbRows} />
              <StatRow label="✅ Presenti in entrambi (match CF)" value={stats.match} valueColor="#1b5e20" />
              <StatRow label="➕ Solo nel file Excel (da aggiungere)" value={stats.onlyExcel} valueColor="#ef6c00" highlight={stats.onlyExcel > 0} />
              <StatRow label="➖ Solo nel DB (assenti dal file)" value={stats.onlyDb} valueColor="#c62828" highlight={stats.onlyDb > 0} />
              <StatRow label="⚠ Con differenze campo (su match)" value={stats.differences} valueColor="#bf360c" highlight={stats.differences > 0} />
            </tbody>
          </table>
          <div style={{ fontSize: 11, color: "#37474f", marginTop: 10, fontStyle: "italic" }}>
            ℹ Il file scaricato contiene 4 fogli: Riepilogo · Da_Aggiungere · Solo_in_DB · Differenze.
            Apri il file per esaminare i dettagli e decidere caso per caso quali integrazioni applicare.
          </div>
        </div>
      )}
    </div>
  );
}

function StatRow({ label, value, valueColor = "#0d47a1", highlight = false }) {
  return (
    <tr style={highlight ? { background: "#fff8e1" } : null}>
      <td style={{ padding: "5px 8px", borderBottom: "1px solid #cfd8dc" }}>{label}</td>
      <td style={{ padding: "5px 8px", borderBottom: "1px solid #cfd8dc",
                   textAlign: "right", fontWeight: 800, fontSize: 16,
                   color: valueColor, fontFamily: "monospace" }}>
        {value.toLocaleString("it-IT")}
      </td>
    </tr>
  );
}
